<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="mx-auto px-4 sm:px-5 lg:px-6 max-w-[1600px]">
      <!-- Header with Return Button -->
      <div class="mb-8 flex flex-col items-center">
        <Title align="center">Daily Tracking</Title>

        <div
          v-if="hasDrinkingPredictions"
          class="mt-4 inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1"
          role="tablist"
          aria-label="Daily tracking view"
        >
          <button
            type="button"
            role="tab"
            :aria-selected="trackingView === 'adherence'"
            @click="trackingView = 'adherence'"
            :class="[
              'px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap',
              trackingView === 'adherence'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            ]"
          >
            Adherence & Biosensor Health
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="trackingView === 'drinking'"
            @click="trackingView = 'drinking'"
            :class="[
              'px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap',
              trackingView === 'drinking'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            ]"
          >
            Drinking Predictions
          </button>
        </div>

        <div class="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
          <span class="text-gray-500">Subject</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ subjectInfo.subid || 'N/A' }}</span>
          <span class="text-gray-300">•</span>
          <span class="text-gray-500">Dataset</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ subjectInfo.dataset_identifier || 'N/A' }}</span>
          <span class="text-gray-300">•</span>
          <span class="text-gray-500">Days</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ dayLevelData.length || 0 }}</span>
        </div>
      </div>

      <template v-if="trackingView === 'adherence'">
      <!-- Device Adherence Overview (summary tiles) -->
      <div class="bg-white rounded-lg shadow p-6 mb-8 w-[85%] mx-auto border-2 border-gray-400">
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 place-items-center">
          <!-- Device Powered On -->
          <div class="text-center">
            <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <img src="../../icons/power-symbol-svgrepo-com.svg" alt="Powered On" class="w-6 h-6" />
            </div>
            <h3 class="text-base font-medium text-gray-900">Device Powered On</h3>
            <p class="text-2xl font-bold text-blue-600">{{ averageDeviceTurnedOn }}%</p>
            <p class="text-xs text-blue-600 font-medium">{{ averageDeviceTurnedOnHours }}h/day</p>
          </div>
          <!-- Device Worn -->
          <div class="text-center">
            <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <img src="../../icons/smartwatch-svgrepo-com.svg" alt="Device Worn" class="w-6 h-6" />
            </div>
            <h3 class="text-base font-medium text-gray-900">Device Worn</h3>
            <p class="text-2xl font-bold text-green-600">{{ averageDeviceWorn }}%</p>
            <p class="text-xs text-green-600 font-medium">{{ averageDeviceWornHours }}h/day</p>
          </div>
          <!-- Jumps -->
          <div class="text-center">
            <div class="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg class="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 17l6-6 4 4 8-8" transform="rotate(-25 12 12)" />
              </svg>
            </div>
            <h3 class="text-base font-medium text-gray-900">Jumps</h3>
            <p class="text-2xl font-bold text-sky-600">{{ averageJumpsPct }}%</p>
            <p class="text-xs text-sky-600 font-medium">{{ averageJumpsHours }}h/day</p>
          </div>
          <!-- Plummets -->
          <div class="text-center">
            <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7l6 6 4-4 8 8" transform="rotate(25 12 12)" />
              </svg>
            </div>
            <h3 class="text-base font-medium text-gray-900">Plummets</h3>
            <p class="text-2xl font-bold text-orange-600">{{ averagePlummetsPct }}%</p>
            <p class="text-xs text-orange-600 font-medium">{{ averagePlummetsHours }}h/day</p>
          </div>
          <!-- Extreme Negative -->
          <div class="text-center">
            <div class="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg class="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
            <h3 class="text-base font-medium text-gray-900">Extreme Negative</h3>
            <p class="text-2xl font-bold text-slate-600">{{ averageExtremeNegativePct }}%</p>
            <p class="text-xs text-slate-600 font-medium">{{ averageExtremeNegativeHours }}h/day</p>
          </div>
        </div>
      </div>

      <!-- Adherence & Health Tracker -->
      <div class="bg-white rounded-lg shadow p-6 mb-8 border-2 border-gray-400 w-fit max-w-full mx-auto">
            <div class="flex flex-col lg:flex-row lg:items-stretch gap-6 px-2 sm:px-4">
              <!-- Days Summary Box -->
              <div class="bg-gray-50 rounded-lg p-4 w-88 border-2 border-gray-300 shrink-0 flex flex-col">
                <Header class="mb-3" align="center">Summary</Header>
                <div class="flex items-center gap-6 flex-1">
                  <!-- Day Count Section with Download Button -->
                  <div class="flex flex-col items-center">
                    <div class="flex items-center">
                      <div class="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-2">
                        <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 class="text-base font-semibold text-gray-900">{{ dayLevelData.length || 0 }} Days</h3>
                        <p class="text-xs text-indigo-600 font-medium">{{ usedDaysCount }}/{{ dayLevelData.length || 0 }} included</p>
                      </div>
                    </div>
                    
                    <!-- Download Button -->
                    <div class="mt-2">
                      <button
                        @click="downloadReport"
                        :disabled="!canExport"
                        class="px-3 py-1.5 bg-slate-100 text-blue-700 text-xs font-medium rounded-md border border-slate-300 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-800 transition-colors duration-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
                        :title="canExport ? 'Download adherence report' : 'No data available for export'"
                      >
                        Download Report
                      </button>
                    </div>
                  </div>
                  
                  <!-- Issue Counts Section -->
                  <div class="space-y-1 ml-2">
                    <div class="flex items-start">
                      <span class="text-xs text-gray-500 w-36 shrink-0">Adherent &amp; Healthy:</span>
                      <span class="text-sm text-black ml-3">{{ adherentAndHealthyDaysCount }} ({{ adherentAndHealthyPercentage }}%)</span>
                    </div>
                    <div class="flex items-start">
                      <span class="text-xs text-gray-500 w-36 shrink-0">Non-adherent:</span>
                      <span class="text-sm text-black ml-3">{{ nonAdherentDaysCount }} ({{ nonAdherentPercentage }}%)</span>
                    </div>
                    <div class="flex items-start">
                      <span class="text-xs text-gray-500 w-36 shrink-0">Low health:</span>
                      <span class="text-sm text-black ml-3">{{ lowHealthDaysCount }} ({{ lowHealthPercentage }}%)</span>
                    </div>
                    <div class="flex items-start">
                      <span class="text-xs text-gray-500 w-36 shrink-0">Excluded:</span>
                      <span class="text-sm text-black ml-3">{{ excludedDaysCount }} ({{ excludedPercentage }}%)</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Controls Container Box -->
              <div class="bg-gray-50 rounded-lg px-5 py-4 border-2 border-gray-300 w-fit max-w-full flex flex-col">
                <div class="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8 flex-1">
                  <!-- Adherence section -->
                  <div class="shrink-0 px-1">
                    <Header class="mb-3" align="center">Biosensor Adherence</Header>
                    <div class="flex flex-col items-center">
                      <div class="relative group mb-2">
                        <label class="text-sm text-gray-600 text-center cursor-help flex flex-col items-center leading-snug">
                          <span>Device Worn</span>
                          <span class="text-xs text-gray-500">(Minimum Allowed)</span>
                        </label>
                        <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal w-64 z-10">
                          The <strong>minimum</strong> number of hours the device must be worn within a day to be considered <strong>adherent</strong>. This threshold is also used to highlight the Powered On and Worn columns in the daily breakdown.
                          <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                      <StandardBlueDropdown class="w-32 md:w-36" v-model="tempThresholds.deviceWornHours" :options="hoursOptions" display-suffix="hours" :suffix-right="true" />
                    </div>
                  </div>
                  <!-- Biosensor Health section -->
                  <div class="shrink-0 px-1">
                    <Header class="mb-3" align="center">Biosensor Health</Header>
                    <div class="flex flex-col items-center">
                      <div class="relative group mb-2">
                        <label class="text-sm text-gray-600 text-center cursor-help flex flex-col items-center leading-snug">
                          <span>Extreme Negative TAC</span>
                          <span class="text-xs text-gray-500">(Maximum Allowed)</span>
                        </label>
                        <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal w-64 z-10">
                          The <strong>maximum</strong> number of hours of extreme negative TAC allowed within a day for the device to be considered <strong>healthy</strong>
                          <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                      <StandardBlueDropdown class="w-32 md:w-36" v-model="tempThresholds.extremeNegativeMaxHours" :options="extremeNegativeHoursOptions" display-suffix="hours" :suffix-right="true" />
                    </div>
                  </div>
                  <!-- Actions -->
                  <div class="flex flex-col items-center justify-center gap-2 shrink-0">
                    <button
                      @click="openResetConfirm('adherence')"
                      :disabled="isAdherenceAtDefaults"
                      class="border border-gray-300 text-gray-700 px-3 h-9 rounded-md text-sm leading-none inline-flex items-center justify-center hover:bg-gray-50 transition-colors min-w-[5.5rem] disabled:text-gray-500 disabled:border-gray-300 disabled:bg-white disabled:cursor-not-allowed disabled:hover:bg-white"
                    >
                      Reset
                    </button>
                    <button
                      @click="applyThresholds"
                      :disabled="adherenceApplying || isAdherenceUnchanged"
                      class="bg-blue-600 text-white px-3 h-9 rounded-md text-sm leading-none inline-flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors min-w-[5.5rem] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg v-if="adherenceApplying" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                      <span>{{ adherenceApplying ? 'Applying...' : 'Apply' }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
      </div>


      

      <!-- REMOVE: Duplicate Daily Breakdown (now inside accordion) -->
      <!--
      <div class="bg-white rounded-lg shadow p-6 mb-8">
        <h2 class="text-xl font-semibold text-gray-900 mb-4">Daily Breakdown</h2>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Powered On</th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Worn</th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Extreme Negative</th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Adherence</th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Biosensor Health</th>
              </tr>
            </thead>
            <tbody v-for="day in dayLevelData" :key="day.date" class="bg-white divide-y divide-gray-200">
              <tr @click="toggleRow(day)" class="hover:bg-gray-50 cursor-pointer" :class="expandedRowDate === day.date ? 'bg-gray-50' : ''">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                  {{ formatDate(day.date) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  <div class="flex items-center justify-center">
                    <div class="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        class="bg-blue-600 h-2 rounded-full" 
                        :style="{ width: Math.min((((day.device_turned_on_duration || 0) / 24) * 100), 100) + '%' }"
                      ></div>
                    </div>
                    <span class="text-xs" :class="getOnTextClass(day)">{{ safeToFixed(day.device_turned_on_duration, 1) }}h ({{ safeToFixed((Number(day.device_turned_on_duration || 0) / 24) * 100, 1) }}%)</span>
                  </div>
                  
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  <div class="flex items-center justify-center">
                    <div class="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        class="bg-green-600 h-2 rounded-full" 
                        :style="{ width: Math.min((((day.device_worn_duration || 0) / 24) * 100), 100) + '%' }"
                      ></div>
                    </div>
                    <span class="text-xs" :class="getWornTextClass(day)">{{ safeToFixed(day.device_worn_duration, 1) }}h ({{ safeToFixed((Number(day.device_worn_duration || 0) / 24) * 100, 1) }}%)</span>
                  </div>
                  
                </td>

                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  <div class="flex items-center justify-center">
                    <div class="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        class="bg-red-600 h-2 rounded-full" 
                        :style="{ width: Math.min((day.extreme_negative_percentage_of_turned_on || 0) * 100, 100) + '%' }"
                      ></div>
                    </div>
                    <span class="text-xs" :class="getExtremeNegTextClass(day)">{{ safeToFixed(day.extreme_negative_duration, 1) }}h ({{ safeToFixed((day.extreme_negative_percentage_of_turned_on || 0) * 100, 1) }}%)</span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  <span 
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="getAdherenceBadgeClass(day)"
                  >
                    {{ getAdherenceLabel(day) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  <span 
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="getBiosensorHealthBadgeClass(day)"
                  >
                    {{ getBiosensorHealthLabel(day) }}
                  </span>
                </td>
              </tr>
              <tr v-if="expandedRowDate === day.date" class="bg-gray-50">
                <td :colspan="6" class="px-6 py-4">
                  <div class="p-4 border border-gray-200 rounded-lg bg-white">
                    <div v-if="expandedDayLoading" class="text-sm text-gray-500">Loading day data...</div>
                    <div v-else-if="expandedDayError" class="text-sm text-red-600">{{ expandedDayError }}</div>
                    <SignalProcessingChart
                      v-else-if="expandedComponent && expandedDayData && expandedDayData.length"
                      :filtered-data="expandedDayData"
                      :curve-features="[]"
                      :selected-curve-index="''"
                      :hide-raw-low-quality-with-imputation="false"
                      :header-title="`Signal Processing — ${formatDate(day.date)}`"
                      :show-controls="true"
                      :width="900"
                      :height="400"
                      :auto-update="true"
                      :timeline-start="computeTimelineStart(expandedDayData)"
                      :timeline-end="computeTimelineEnd(expandedDayData)"
                    />
                    <div v-else class="text-sm text-gray-500">No data available for this day.</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      -->

      <!-- REMOVE: Duplicate Histograms (now inside accordion) -->
      <!--
      <div class="mt-6 mb-8">
        
        <div class="inline-flex rounded-lg bg-gray-100 p-1" role="tablist" aria-label="Histogram Toggle">
          <button
            @click="selectHistogram('Temperature_C')"
            :aria-pressed="histogram.active === 'Temperature_C'"
            :class="histogram.active === 'Temperature_C' ? 'bg-white text-blue-600 shadow' : 'text-gray-700 hover:text-gray-900'"
            class="px-4 py-1.5 text-sm font-medium rounded-md focus:outline-none"
            role="tab"
          >
            Temperature
          </button>
          <button
            @click="selectHistogram('Motion')"
            :aria-pressed="histogram.active === 'Motion'"
            :class="histogram.active === 'Motion' ? 'bg-white text-blue-600 shadow' : 'text-gray-700 hover:text-gray-900'"
            class="ml-1 px-4 py-1.5 text-sm font-medium rounded-md focus:outline-none"
            role="tab"
          >
            Motion
          </button>
        </div>
        <div v-if="histogram.error" class="mt-2 text-sm text-red-600">{{ histogram.error }}</div>
        <div class="bg-gray-50 rounded p-4 h-96 mt-4">
          <div v-if="histogram.loading" class="flex items-center text-sm text-gray-500">
            <svg class="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            Loading histogram…
          </div>
          <BaseChart
            v-else-if="histogram.chartData && histogram.chartOptions"
            ref="histChart"
            :chart-type="'bar'"
            :chart-data="histogram.chartData"
            :chart-options="histogram.chartOptions"
            :chart-plugins="histogram.chartPlugins"
            :width="800"
            :height="360"
            :auto-update="true"
          />
          <div v-else class="text-sm text-gray-500">No histogram data to display.</div>
        </div>
      </div>
      -->
      </template>

      <template v-else-if="hasDrinkingPredictions">
      <!-- Drinking predictions overview (summary tiles) -->
      <div class="bg-white rounded-lg shadow p-6 mb-8 w-[85%] mx-auto border-2 border-gray-400">
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 place-items-center">
          <!-- Drinking Days -->
          <div class="text-center">
            <div class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg class="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 512 512">
                <path d="M352 96h-13.6C326.7 39.8 274.7 0 213.3 0c-61.3 0-113.3 39.8-125 96H64c-17.7 0-32 14.3-32 32v32c0 26.5 10.7 50.5 28 68v156c0 35.3 28.7 64 64 64h192c35.3 0 64-28.7 64-64V228c17.3-17.5 28-41.5 28-68v-32c0-17.7-14.3-32-32-32zm-64 288c0 17.6-14.4 32-32 32H128c-17.6 0-32-14.4-32-32V240h192v144zm0-176H96v-16h192v16zm64-16c0 8.8-7.2 16-16 16h-16v-16h32z"/>
                <ellipse cx="128" cy="80" rx="24" ry="16"/>
                <ellipse cx="192" cy="64" rx="28" ry="20"/>
                <ellipse cx="264" cy="76" rx="24" ry="16"/>
              </svg>
            </div>
            <h3 class="text-base font-medium text-gray-900">Drinking Days</h3>
            <p class="text-2xl font-bold text-purple-600">{{ predictedDrinkingDaysCount }}/{{ dayLevelData.length }}</p>
            <p class="text-xs text-purple-600 font-medium">days ({{ predictedDrinkingDaysPct }}%)</p>
          </div>
          <!-- Device Powered On -->
          <div class="text-center">
            <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <img src="../../icons/power-symbol-svgrepo-com.svg" alt="Powered On" class="w-6 h-6" />
            </div>
            <h3 class="text-base font-medium text-gray-900">Device Powered On</h3>
            <p class="text-2xl font-bold text-blue-600">{{ averageDeviceTurnedOn }}%</p>
            <p class="text-xs text-blue-600 font-medium">{{ averageDeviceTurnedOnHours }}h/day</p>
          </div>
          <!-- Device Worn -->
          <div class="text-center">
            <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <img src="../../icons/smartwatch-svgrepo-com.svg" alt="Device Worn" class="w-6 h-6" />
            </div>
            <h3 class="text-base font-medium text-gray-900">Device Worn</h3>
            <p class="text-2xl font-bold text-green-600">{{ averageDeviceWorn }}%</p>
            <p class="text-xs text-green-600 font-medium">{{ averageDeviceWornHours }}h/day</p>
          </div>
          <!-- Jumps -->
          <div class="text-center">
            <div class="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg class="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 17l6-6 4 4 8-8" transform="rotate(-25 12 12)" />
              </svg>
            </div>
            <h3 class="text-base font-medium text-gray-900">Jumps</h3>
            <p class="text-2xl font-bold text-sky-600">{{ averageJumpsPct }}%</p>
            <p class="text-xs text-sky-600 font-medium">{{ averageJumpsHours }}h/day</p>
          </div>
          <!-- Plummets -->
          <div class="text-center">
            <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7l6 6 4-4 8 8" transform="rotate(25 12 12)" />
              </svg>
            </div>
            <h3 class="text-base font-medium text-gray-900">Plummets</h3>
            <p class="text-2xl font-bold text-orange-600">{{ averagePlummetsPct }}%</p>
            <p class="text-xs text-orange-600 font-medium">{{ averagePlummetsHours }}h/day</p>
          </div>
          <!-- Extreme Negative -->
          <div class="text-center">
            <div class="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg class="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
            <h3 class="text-base font-medium text-gray-900">Extreme Negative</h3>
            <p class="text-2xl font-bold text-slate-600">{{ averageExtremeNegativePct }}%</p>
            <p class="text-xs text-slate-600 font-medium">{{ averageExtremeNegativeHours }}h/day</p>
          </div>
        </div>
      </div>

      <!-- Drinking Predictions Tracker -->
      <div class="bg-white rounded-lg shadow p-6 mb-8 border-2 border-gray-400">
        <div class="flex flex-col lg:flex-row lg:items-stretch gap-6 px-4 lg:px-8">
          <!-- Left: Summary Breakdown -->
          <div class="bg-gray-50 rounded-lg px-4 py-3 border-2 border-gray-300 flex flex-col" style="min-width: 18rem;">
            <Header class="mb-3" align="center">Summary</Header>
            <div class="grid grid-cols-2 gap-x-0 flex-1">
              <!-- Drinking column -->
              <div class="pr-4 text-center">
                <h4 class="text-sm font-semibold text-purple-700 mb-2 tabular-nums">Drinking ({{ drinkingDays.length }})</h4>
                <div class="space-y-1">
                  <div class="flex items-baseline justify-between gap-3 text-left">
                    <span class="text-xs text-gray-500">High-confidence:</span>
                    <span class="text-xs font-medium text-gray-800 tabular-nums">{{ drinkingHighConfidence.length }}</span>
                  </div>
                  <div class="flex items-baseline justify-between gap-3 text-left">
                    <span class="text-xs text-gray-500">Low-confidence:</span>
                    <span class="text-xs font-medium text-gray-800 tabular-nums">{{ drinkingLowConfidence.length }}</span>
                  </div>
                </div>
              </div>
              <!-- Non-Drinking column -->
              <div class="pl-4 border-l border-gray-200 text-center">
                <h4 class="text-sm font-semibold text-gray-600 mb-2 tabular-nums">Non-Drinking ({{ nonDrinkingDays.length }})</h4>
                <div class="space-y-1">
                  <div class="flex items-baseline justify-between gap-3 text-left">
                    <span class="text-xs text-gray-500">High-confidence:</span>
                    <span class="text-xs font-medium text-gray-800 tabular-nums">{{ nonDrinkingHighConfidence.length }}</span>
                  </div>
                  <div class="flex items-baseline justify-between gap-3 text-left">
                    <span class="text-xs text-gray-500">Low-confidence:</span>
                    <span class="text-xs font-medium text-gray-800 tabular-nums">{{ nonDrinkingLowConfidence.length }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-auto pt-3 border-t border-gray-200 flex justify-center">
              <button
                @click="downloadDayLevelData"
                :disabled="!canExportDayLevelData"
                class="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-md hover:bg-purple-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                :title="canExportDayLevelData ? 'Select columns and export day-level data' : 'No data available for export'"
              >
                Export Day-Level Data
              </button>
            </div>
          </div>

          <!-- Right: Prediction Threshold + Data Quality side by side -->
          <div class="flex-1 flex flex-col lg:flex-row lg:items-stretch gap-3 min-w-0">
            <!-- Prediction Threshold Slider (+ uncertainty bounds) -->
            <div class="bg-gray-50 rounded-lg p-3 border-2 border-gray-300 lg:flex-[2] min-w-0 flex flex-col">
              <div class="flex-1 min-w-0">
                <Header class="mb-3" align="center">
                  <TeleportTooltip
                    label="Prediction Threshold"
                    label-class="text-base font-semibold tracking-tight font-inter text-gray-700 cursor-help border-b border-dotted border-gray-400"
                  >
                    <p><strong>Circle marker:</strong> probability cutoff for classifying a day as drinking. Raising it classifies fewer days as drinking; lowering it classifies more.</p>
                    <p class="mt-2"><strong>Uncertainty range:</strong> vertical line markers and the shaded band between them show the low-confidence probability range.</p>
                  </TeleportTooltip>
                </Header>
                <div class="prediction-threshold-slider relative">
                  <div
                    class="prediction-threshold-value"
                    :style="{ left: predictionValueToPct(tempPredictionThreshold) + '%' }"
                  >
                    {{ Number(tempPredictionThreshold).toFixed(2) }}
                  </div>
                  <div ref="predictionSliderTrack" class="prediction-slider-track relative">
                    <div class="prediction-uncertain-band" :style="uncertainBandStyle" aria-hidden="true" />
                    <div
                      class="prediction-bound-marker"
                      :style="{ left: predictionValueToPct(tempMaptacThresholds.probaUnreliableLow) + '%' }"
                      @pointerdown="startBoundDrag('low', $event)"
                    >
                      <TeleportTooltip description="Uncertainty Lower Bound" max-width="14rem">
                        <template #trigger>
                          <span class="prediction-bound-hit" role="img" aria-label="Uncertainty Lower Bound">
                            <span class="prediction-bound-label">{{ Number(tempMaptacThresholds.probaUnreliableLow).toFixed(2) }}</span>
                            <span class="prediction-bound-line" aria-hidden="true" />
                          </span>
                        </template>
                      </TeleportTooltip>
                    </div>
                    <div
                      class="prediction-bound-marker"
                      :style="{ left: predictionValueToPct(tempMaptacThresholds.probaUnreliableHigh) + '%' }"
                      @pointerdown="startBoundDrag('high', $event)"
                    >
                      <TeleportTooltip description="Uncertainty Higher Bound" max-width="14rem">
                        <template #trigger>
                          <span class="prediction-bound-hit" role="img" aria-label="Uncertainty Higher Bound">
                            <span class="prediction-bound-label">{{ Number(tempMaptacThresholds.probaUnreliableHigh).toFixed(2) }}</span>
                            <span class="prediction-bound-line" aria-hidden="true" />
                          </span>
                        </template>
                      </TeleportTooltip>
                    </div>
                    <input
                      ref="predictionThresholdInput"
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      :value="tempPredictionThreshold"
                      @input="onThresholdInput"
                      @change="onThresholdInput"
                      class="w-full appearance-none cursor-pointer accent-purple-600 relative z-20"
                      :style="predictionSliderStyle"
                    />
                  </div>
                  <div class="flex justify-between mt-1 relative z-0">
                    <span class="text-xs text-gray-400">0.00</span>
                    <span class="text-xs text-gray-400">0.50</span>
                    <span class="text-xs text-gray-400">1.00</span>
                  </div>
                </div>
              </div>
              <div class="mt-auto pt-3 flex items-center justify-center gap-2">
                <button
                  @click="openResetConfirm('prediction')"
                  :disabled="isPredictionThresholdAtDefault"
                  class="border border-gray-300 text-gray-700 px-3 h-9 rounded-md text-sm leading-none inline-flex items-center justify-center hover:bg-gray-50 transition-colors min-w-[5.5rem] disabled:text-gray-500 disabled:border-gray-300 disabled:bg-white disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Reset
                </button>
                <button
                  @click="applyPredictionThreshold"
                  :disabled="predictionThresholdApplying || isPredictionUnchanged"
                  class="bg-purple-600 text-white px-3 h-9 rounded-md text-sm leading-none inline-flex items-center justify-center gap-2 hover:bg-purple-700 transition-colors min-w-[5.5rem] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg v-if="predictionThresholdApplying" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                  <span>{{ predictionThresholdApplying ? 'Applying...' : 'Apply' }}</span>
                </button>
              </div>
            </div>

            <!-- Data Quality Warnings -->
            <div class="bg-gray-50 rounded-lg p-3 border-2 border-gray-300 lg:flex-[3] min-w-0 flex flex-col">
              <div class="flex-1 min-w-0">
                <Header class="mb-3" align="center">
                  <TeleportTooltip
                    label="Data Quality Warnings"
                    label-class="text-base font-semibold tracking-tight font-inter text-gray-700 cursor-help border-b border-dotted border-gray-400"
                    max-width="26rem"
                  >
                    When days contain a lot of low-quality data, algorithm predictions may be misleading or unreliable. Use the thresholds below to catch different types of low-quality days.
                  </TeleportTooltip>
                </Header>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-3">
                  <div class="flex flex-col items-center">
                    <TeleportTooltip
                      variant="plain"
                      label="Device Worn (Minimum)"
                      description="Minimum worn hours; days below this are flagged as potentially unreliable"
                      class="mb-1"
                    />
                    <StandardBlueDropdown class="w-28 md:w-32" v-model="tempMaptacThresholds.deviceWornHoursMin" :options="maptacWornHoursOptions" display-suffix="hours" :suffix-right="true" />
                  </div>
                  <div class="flex flex-col items-center">
                    <TeleportTooltip
                      variant="plain"
                      label="Artifacts (Minimum)"
                      description="Minimum combined jump + plummet hours to flag a day"
                      class="mb-1"
                    />
                    <StandardBlueDropdown class="w-28 md:w-32" v-model="tempMaptacThresholds.artifactHoursMin" :options="maptacHoursOptions" display-suffix="hours" :suffix-right="true" />
                  </div>
                  <div class="flex flex-col items-center">
                    <TeleportTooltip
                      variant="plain"
                      label="Very Negative TAC (Min)"
                      description="Minimum hours of extreme negative TAC (&lt;−15) to flag a day"
                      class="mb-1"
                    />
                    <StandardBlueDropdown class="w-28 md:w-32" v-model="tempMaptacThresholds.veryNegativeTacHoursMin" :options="maptacHoursOptions" display-suffix="hours" :suffix-right="true" />
                  </div>
                </div>
              </div>
              <div class="mt-auto pt-3 flex items-center justify-center gap-2">
                <button
                  @click="openResetConfirm('maptac')"
                  :disabled="isMaptacAtDefaults"
                  class="border border-gray-300 text-gray-700 px-3 h-9 rounded-md text-sm leading-none inline-flex items-center justify-center hover:bg-gray-50 transition-colors min-w-[5.5rem] disabled:text-gray-500 disabled:border-gray-300 disabled:bg-white disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Reset
                </button>
                <button
                  @click="applyMaptacThresholds"
                  :disabled="maptacApplying || isMaptacHoursUnchanged"
                  class="bg-blue-600 text-white px-3 h-9 rounded-md text-sm leading-none inline-flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors min-w-[5.5rem] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg v-if="maptacApplying" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                  <span>{{ maptacApplying ? 'Applying...' : 'Apply' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </template>

      <!-- Daily Breakdown Table -->
      <div class="bg-white rounded-lg shadow p-0 mb-8 border-2 border-gray-400 overflow-hidden">
        <button
          @click="toggleTableExpansion"
          class="relative z-10 w-full flex items-center justify-center px-6 py-2.5 bg-slate-800 hover:bg-slate-700 transition-colors border-b border-slate-700"
        >
          <div class="flex items-center">
            <h3 class="text-xl font-semibold text-slate-200">Daily Breakdown</h3>
            <span class="ml-2 text-base text-slate-200">({{ dayLevelData.length }} days)</span>
          </div>
          <svg
            class="absolute right-6 w-5 h-5 text-slate-400 transition-transform duration-200"
            :class="isTableExpanded ? 'rotate-180' : ''"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div v-show="isTableExpanded" class="overflow-x-auto" style="overflow-y: visible;">
          <table class="daily-breakdown-table w-max min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50 relative" style="z-index: 60;">
              <tr class="align-bottom">
                <th class="db-col-day pb-3 pt-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Day</th>
                <th class="db-col-time pb-3 pt-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Start Time</th>
                <th class="db-col-time pb-3 pt-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider" :class="{ 'db-before-group': trackingView === 'adherence' }">End Time</th>
                <th v-if="trackingView === 'drinking'" class="db-col-prediction pb-3 pt-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Drinking Detected?</th>
                <th class="db-col-metric pb-3 pt-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider" :class="{ 'db-group-adherence-start': trackingView === 'adherence' }">Powered On</th>
                <th class="db-col-metric pb-3 pt-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider" :class="{ 'db-group-adherence-mid': trackingView === 'adherence' }">Worn</th>
                <th v-if="trackingView === 'drinking'" class="db-col-metric pb-3 pt-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider overflow-visible align-bottom">
                  <TeleportTooltip label="Artifacts">
                    Combined jumps and plummets (abrupt TAC changes)
                  </TeleportTooltip>
                </th>
                <th v-if="trackingView === 'adherence'" class="db-col-badge db-group-adherence-end pb-3 pt-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Adherence</th>
                <th
                  class="db-col-metric pb-3 pt-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider overflow-visible align-bottom"
                  :class="{ 'db-group-health-start': trackingView === 'adherence' }"
                >
                  <TeleportTooltip label="Extreme Negative">
                    Very negative TAC (&lt;−15 µg/L); indicates possible sensor failure
                  </TeleportTooltip>
                </th>
                <th v-if="trackingView === 'adherence'" class="db-col-badge db-group-health-end pb-3 pt-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Biosensor Health</th>
                <th v-if="trackingView === 'adherence'" class="db-col-exclude pb-3 pt-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Exclude</th>
              </tr>
            </thead>
            <tbody v-for="(day, dayIndex) in dayLevelData" :key="day.date" class="bg-white divide-y divide-gray-200">
              <tr
                @click="toggleRow(day)"
                class="cursor-pointer transition-[filter,opacity,background-color]"
                :class="(day.excluded && trackingView === 'adherence')
                  ? 'db-row-excluded'
                  : (expandedRowDate === day.date ? 'bg-gray-50 hover:bg-gray-50' : 'hover:bg-gray-50')"
              >
                <td class="db-col-day py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">{{ dayIndex + 1 }}</td>
                <td class="db-col-time py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  <span class="day-time-parts" v-if="parseDayTime(day.begin_day)">
                    <span class="font-semibold text-gray-800">{{ parseDayTime(day.begin_day).dow }}</span>
                    <span class="text-gray-400 mx-0.5">·</span>
                    <span class="font-semibold text-gray-800">{{ parseDayTime(day.begin_day).date }}</span>
                    <span class="text-gray-400 mx-0.5">·</span>
                    <span class="font-semibold text-gray-800">{{ parseDayTime(day.begin_day).time }}</span>
                  </span>
                  <span v-else class="text-gray-400">N/A</span>
                </td>
                <td class="db-col-time py-4 whitespace-nowrap text-sm text-gray-900 text-center" :class="{ 'db-before-group': trackingView === 'adherence' }">
                  <span class="day-time-parts" v-if="parseDayTime(day.end_day)">
                    <span class="font-semibold text-gray-800">{{ parseDayTime(day.end_day).dow }}</span>
                    <span class="text-gray-400 mx-0.5">·</span>
                    <span class="font-semibold text-gray-800">{{ parseDayTime(day.end_day).date }}</span>
                    <span class="text-gray-400 mx-0.5">·</span>
                    <span class="font-semibold text-gray-800">{{ parseDayTime(day.end_day).time }}</span>
                  </span>
                  <span v-else class="text-gray-400">N/A</span>
                </td>
                <td v-if="trackingView === 'drinking'" class="db-col-prediction py-4 text-sm text-gray-900">
                  <DayMetricBar
                    mode="prediction"
                    :percent="getDrinkingDetectionForDay(day).percent"
                    :verdict="getDrinkingDetectionForDay(day).verdict"
                    :bar-color="getDrinkingDetectionForDay(day).barColor"
                    :text-class="getPredictionTextClass(day)"
                    :warning-reasons="getPredictionWarningReasons(day)"
                  />
                </td>
                <td class="db-col-metric py-4 text-sm text-gray-900 text-center" :class="{ 'db-group-adherence-start': trackingView === 'adherence' }">
                  <DayMetricBar
                    :duration-hours="day.device_turned_on_duration"
                    :percent="day.device_turned_on_percent_of_day"
                    bar-color="bg-blue-600"
                    :text-class="getOnTextClass(day)"
                  />
                </td>
                <td class="db-col-metric py-4 text-sm text-gray-900 text-center" :class="{ 'db-group-adherence-mid': trackingView === 'adherence' }">
                  <DayMetricBar
                    :duration-hours="day.device_worn_duration"
                    :percent="day.device_worn_percent_of_day"
                    bar-color="bg-green-600"
                    :text-class="getWornTextClass(day)"
                  />
                </td>
                <td v-if="trackingView === 'drinking'" class="db-col-metric py-4 text-sm text-gray-900">
                  <DayMetricBar
                    :duration-hours="getArtifactDurationHours(day)"
                    :percent="getArtifactPercentOfDay(day)"
                    bar-color="bg-amber-600"
                    :text-class="getArtifactsTextClass(day)"
                  />
                </td>
                <td v-if="trackingView === 'adherence'" class="db-col-badge db-group-adherence-end py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  <span class="px-2 py-1 text-xs font-medium rounded-full" :class="getAdherenceBadgeClass(day)">{{ getAdherenceLabel(day) }}</span>
                </td>
                <td class="db-col-metric py-4 text-sm text-gray-900 text-center" :class="{ 'db-group-health-start': trackingView === 'adherence' }">
                  <DayMetricBar
                    :duration-hours="day.extreme_negative_duration"
                    :percent="day.extreme_negative_percent_of_day"
                    bar-color="bg-red-600"
                    :text-class="getExtremeNegTextClass(day)"
                  />
                </td>
                <td v-if="trackingView === 'adherence'" class="db-col-badge db-group-health-end py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  <span class="px-2 py-1 text-xs font-medium rounded-full" :class="getBiosensorHealthBadgeClass(day)">{{ getBiosensorHealthLabel(day) }}</span>
                </td>
                <td v-if="trackingView === 'adherence'" class="db-col-exclude py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  <input type="checkbox" :checked="!!day.excluded" @click.stop="toggleExcludeDay(day)" />
                </td>
              </tr>
              <tr
                v-if="expandedRowDate === day.date"
                :class="(day.excluded && trackingView === 'adherence') ? 'db-row-excluded' : 'bg-gray-50'"
              >
                <td :colspan="dailyBreakdownColspan" class="px-6 py-4">
                  <div v-if="expandedComponent && expandedDayData && expandedDayData.length" class="mb-4 flex items-center justify-center">
                    <div class="flex items-center space-x-4">
                      <span class="text-sm font-medium text-gray-700" :class="{ 'text-blue-600': getDayChartType(day) === 'signal' }">TAC Signal</span>
                      <button
                        @click="toggleDayChartType(day)"
                        class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 bg-blue-200"
                      >
                        <span
                          class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                          :class="getDayChartType(day) === 'nonwear' ? 'translate-x-6' : 'translate-x-1'"
                        ></span>
                      </button>
                      <span class="text-sm font-medium text-gray-700" :class="{ 'text-blue-600': getDayChartType(day) === 'nonwear' }">Non-Wear Detection</span>
                    </div>
                  </div>
                  <div class="p-4 border border-gray-200 rounded-lg bg-white">
                    <div v-if="expandedDayLoading" class="text-sm text-gray-500">Loading day data...</div>
                    <div v-else-if="expandedDayError" class="text-sm text-red-600">{{ expandedDayError }}</div>
                    <div v-else-if="expandedComponent && expandedDayData && expandedDayData.length">
                      <SignalProcessingChart
                        v-if="getDayChartType(day) === 'signal'"
                        :filtered-data="expandedDayData"
                        :curve-features="[]"
                        :selected-curve-index="''"
                        :hide-raw-low-quality-with-imputation="false"
                        :header-title="`TAC Signal - Day ${dayIndex + 1}`"
                        :show-controls="true"
                        :width="900"
                        :height="500"
                        :auto-update="true"
                        :timeline-start="computeTimelineStart(expandedDayData)"
                        :timeline-end="computeTimelineEnd(expandedDayData)"
                        :allow-missing-rows="true"
                      />
                      <NonWearDetectionChart
                        v-else
                        :filtered-data="expandedDayData"
                        :curve-features="[]"
                        :selected-curve-index="''"
                        :header-title="`Non-Wear Detection - Day ${dayIndex + 1}`"
                        :show-controls="true"
                        :width="900"
                        :height="500"
                        :auto-update="true"
                        :timeline-start="computeTimelineStart(expandedDayData)"
                        :timeline-end="computeTimelineEnd(expandedDayData)"
                        :allow-missing-rows="true"
                      />
                    </div>
                    <div v-else class="text-sm text-gray-500">No data available for this day.</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <ColumnSelectionModal
      :isVisible="showDayColumnModal"
      title="Select Day-Level Columns"
      :data="dayLevelData"
      :groups="dayExportGroups"
      :descriptions="dayDescriptions"
      :lockedColumns="dayLockedColumns"
      :presets="dayPresets"
      sheetName="Day Level Data"
      :filename="dayFilename"
      @close="showDayColumnModal = false"
    />

    <BaseModal
      :isVisible="showResetConfirm"
      :title="resetConfirmTitle"
      width="md"
      @close="closeResetConfirm"
    >
      <template #content>
        <p class="text-sm text-gray-600 mb-3">
          This will restore the following defaults and save them for this subject:
        </p>
        <ul class="text-sm text-gray-800 space-y-2">
          <li v-for="item in resetConfirmDefaults" :key="item.label" class="flex justify-between gap-4">
            <span class="text-gray-600">{{ item.label }}</span>
            <span class="font-semibold text-gray-900">{{ item.value }}</span>
          </li>
        </ul>
      </template>
      <template #footer>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            @click="closeResetConfirm"
            :disabled="resetApplying"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="confirmApplyDefaults"
            :disabled="resetApplying"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="resetApplying" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
            <span>{{ resetApplying ? 'Applying...' : 'Apply Defaults' }}</span>
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { DATA_QUALITY_COLORS } from '../../config/chartColors.js'
import { buildApiUrl, API_ENDPOINTS } from '../../config/api.js'
import SignalProcessingChart from '../../components/SignalProcessingChart.vue'
import NonWearDetectionChart from '../../components/NonWearDetectionChart.vue'
import StandardBlueDropdown from '../../components/StandardBlueDropdown.vue'
import BaseChart from '../../components/BaseChart.vue'
import Title from '../../components/Title.vue'
import Header from '../../components/Header.vue'
import DayMetricBar from '../../components/DayMetricBar.vue'
import TeleportTooltip from '../../components/TeleportTooltip.vue'
import ColumnSelectionModal from '../../components/ColumnSelectionModal.vue'
import BaseModal from '../../components/BaseModal.vue'
import { downloadAdherenceReport, validateAdherenceReportData } from '../../utils/adherenceReportExportUtils.js'
import { dataRevisionService } from '../../utils/dataRevisionService.js'
import { ALL_CHART_COLUMNS, createDataCache } from '../../utils/chartDataColumns.js'
import {
  PREDICTION_COLUMNS,
  DAY_EXPORT_FEATURE_GROUPS,
  buildDefaultDayExportColumns,
  buildEssentialDayExportColumns,
  buildAllDayExportColumns,
  isLockedExportColumn
} from '../../utils/dayExportFeatureRegistry.js'
import dayFeatureDescriptions from '../../generated/dayFeatureDescriptions.json'
import {
  buildDrinkingPredictionReview,
  buildDayRevisionPayloads,
  hydrateDrinkingPredictionReview,
  DEFAULT_PREDICTION_THRESHOLD,
} from '../../utils/drinkingPredictionReview.js'
import {
  buildAdherenceReview,
  hydrateAdherenceReview,
  DEFAULT_ADHERENCE_THRESHOLDS,
} from '../../utils/adherenceReview.js'

/** Minimum gap between prediction threshold and either uncertainty bound. */
const MIN_THRESHOLD_BOUND_GAP = 0.05
import {
  MAPTAC_WARNING_THRESHOLDS,
  isUnreliablePredictionDay,
  isPredictedDrinkingDay,
  isPredictedNonDrinkingDay,
  getDrinkingDetectionForDay as resolveDrinkingDetectionForDay,
  artifactDurationHours,
  countWarningDays,
  hasNonWearWarning,
  hasVeryNegativeTacWarning,
  hasArtifactWarning,
  hasUncertainProbaWarning,
  buildReliabilityFlagDescriptions,
  getDrinkingPredictionWarningReasons
} from '../../utils/drinkingPredictionReliability.js'

export default {
  name: 'AdherenceReport',
  inject: ['setBackButton', 'hideBackButton', 'globalState', 'updateGlobalState'],
  components: { SignalProcessingChart, NonWearDetectionChart, StandardBlueDropdown, BaseChart, Title, Header, DayMetricBar, TeleportTooltip, ColumnSelectionModal, BaseModal },
  data() {
    return {
      MAPTAC_WARNING_THRESHOLDS,
      dayLevelData: [],
      subjectInfo: {},
      drinkingDayAlgorithmEnabled: false,
      showDayColumnModal: false,
      showResetConfirm: false,
      resetConfirmKind: null, // 'adherence' | 'prediction' | 'maptac'
      resetApplying: false,
      trackingView: 'adherence',
      expandedRowDate: null,
      expandedComponent: null,
      expandedDayData: null,
      expandedDayLoading: false,
      expandedDayError: null,
      sessionId: null,
      s3Key: null,
      // Chart toggle state for each day
      dayChartToggle: {},
      // Thresholds
      thresholds: { ...DEFAULT_ADHERENCE_THRESHOLDS },
      tempThresholds: { ...DEFAULT_ADHERENCE_THRESHOLDS },
      adherenceApplying: false,
      // Options for selects with 0.5-hour increments
      hoursOptions: Array.from({ length: Math.floor(((23 - 10) / 0.5)) + 1 }, (_, i) => 10 + i * 0.5),
      extremeNegativeHoursOptions: Array.from({ length: Math.floor((24 / 0.5)) + 1 }, (_, i) => 0 + i * 0.5),
      // MAPTAC warning thresholds (interactive)
      maptacThresholds: { ...MAPTAC_WARNING_THRESHOLDS },
      tempMaptacThresholds: { ...MAPTAC_WARNING_THRESHOLDS },
      maptacApplying: false,
      maptacHoursOptions: Array.from({ length: Math.floor((12 - 0.5) / 0.5) + 1 }, (_, i) => 0.5 + i * 0.5),
      maptacWornHoursOptions: Array.from({ length: Math.floor((24 - 6) / 1) + 1 }, (_, i) => 6 + i),
      // Prediction threshold slider
      predictionThreshold: DEFAULT_PREDICTION_THRESHOLD,
      tempPredictionThreshold: DEFAULT_PREDICTION_THRESHOLD,
      predictionThresholdApplying: false,
      boundDrag: null, // { which: 'low'|'high' }
      // Table expansion state
      isTableExpanded: true,
      // Data cache for day charts (Phase 1 optimization)
      dayDataCache: createDataCache(5)  // Cache up to 5 days
    }
  },

  computed: {
    hasDrinkingPredictions() {
      if (this.drinkingDayAlgorithmEnabled) return true
      if (!this.dayLevelData.length) return false
      const sample = this.dayLevelData[0]
      return PREDICTION_COLUMNS.every(col => Object.prototype.hasOwnProperty.call(sample, col))
    },

    dailyBreakdownColspan() {
      return this.trackingView === 'drinking' ? 8 : 9
    },

    predictionSliderStyle() {
      const pct = this.predictionValueToPct(this.tempPredictionThreshold)
      return {
        background: `linear-gradient(to right, #7c3aed ${pct}%, #e5e7eb ${pct}%)`
      }
    },

    uncertainBandStyle() {
      const left = this.predictionValueToPct(this.tempMaptacThresholds.probaUnreliableLow)
      const right = this.predictionValueToPct(this.tempMaptacThresholds.probaUnreliableHigh)
      return {
        left: `${left}%`,
        width: `${Math.max(0, right - left)}%`
      }
    },

    probaLowSnapOptions() {
      const opts = []
      for (let v = 0.01; v <= 0.05; v = Math.round((v + 0.01) * 100) / 100) opts.push(v)
      for (let v = 0.10; v <= 0.50; v = Math.round((v + 0.05) * 100) / 100) opts.push(v)
      return opts
    },

    probaHighSnapOptions() {
      const opts = []
      for (let v = 0.50; v <= 0.90; v = Math.round((v + 0.05) * 100) / 100) opts.push(v)
      for (let v = 0.95; v <= 0.99; v = Math.round((v + 0.01) * 100) / 100) opts.push(v)
      return opts
    },

    isAdherenceAtDefaults() {
      return (
        Number(this.tempThresholds.deviceWornHours) === DEFAULT_ADHERENCE_THRESHOLDS.deviceWornHours
        && Number(this.tempThresholds.extremeNegativeMaxHours) === DEFAULT_ADHERENCE_THRESHOLDS.extremeNegativeMaxHours
      )
    },

    isAdherenceUnchanged() {
      return (
        Number(this.tempThresholds.deviceWornHours) === Number(this.thresholds.deviceWornHours)
        && Number(this.tempThresholds.extremeNegativeMaxHours) === Number(this.thresholds.extremeNegativeMaxHours)
        && Number(this.tempThresholds.deviceOnHours) === Number(this.thresholds.deviceOnHours)
      )
    },

    isMaptacAtDefaults() {
      return (
        Number(this.tempMaptacThresholds.deviceWornHoursMin) === Number(MAPTAC_WARNING_THRESHOLDS.deviceWornHoursMin)
        && Number(this.tempMaptacThresholds.veryNegativeTacHoursMin) === Number(MAPTAC_WARNING_THRESHOLDS.veryNegativeTacHoursMin)
        && Number(this.tempMaptacThresholds.artifactHoursMin) === Number(MAPTAC_WARNING_THRESHOLDS.artifactHoursMin)
      )
    },

    isMaptacHoursUnchanged() {
      return (
        Number(this.tempMaptacThresholds.deviceWornHoursMin) === Number(this.maptacThresholds.deviceWornHoursMin)
        && Number(this.tempMaptacThresholds.veryNegativeTacHoursMin) === Number(this.maptacThresholds.veryNegativeTacHoursMin)
        && Number(this.tempMaptacThresholds.artifactHoursMin) === Number(this.maptacThresholds.artifactHoursMin)
      )
    },

    isPredictionThresholdAtDefault() {
      return (
        Number(this.tempPredictionThreshold) === DEFAULT_PREDICTION_THRESHOLD
        && Number(this.tempMaptacThresholds.probaUnreliableLow) === Number(MAPTAC_WARNING_THRESHOLDS.probaUnreliableLow)
        && Number(this.tempMaptacThresholds.probaUnreliableHigh) === Number(MAPTAC_WARNING_THRESHOLDS.probaUnreliableHigh)
      )
    },

    isPredictionUnchanged() {
      return (
        Number(this.tempPredictionThreshold) === Number(this.predictionThreshold)
        && Number(this.tempMaptacThresholds.probaUnreliableLow) === Number(this.maptacThresholds.probaUnreliableLow)
        && Number(this.tempMaptacThresholds.probaUnreliableHigh) === Number(this.maptacThresholds.probaUnreliableHigh)
      )
    },

    resetConfirmTitle() {
      if (this.resetConfirmKind === 'adherence') return 'Reset Biosensor Adherence & Health?'
      if (this.resetConfirmKind === 'prediction') return 'Reset Prediction Threshold?'
      if (this.resetConfirmKind === 'maptac') return 'Reset Data Quality Warnings?'
      return 'Reset to Defaults?'
    },

    resetConfirmDefaults() {
      if (this.resetConfirmKind === 'adherence') {
        return [
          { label: 'Device Worn', value: `${DEFAULT_ADHERENCE_THRESHOLDS.deviceWornHours} hours` },
          { label: 'Extreme Negative TAC max', value: `${DEFAULT_ADHERENCE_THRESHOLDS.extremeNegativeMaxHours} hours` }
        ]
      }
      if (this.resetConfirmKind === 'prediction') {
        return [
          { label: 'Cutoff', value: Number(DEFAULT_PREDICTION_THRESHOLD).toFixed(2) },
          { label: 'Uncertainty lower', value: Number(MAPTAC_WARNING_THRESHOLDS.probaUnreliableLow).toFixed(2) },
          { label: 'Uncertainty higher', value: Number(MAPTAC_WARNING_THRESHOLDS.probaUnreliableHigh).toFixed(2) }
        ]
      }
      if (this.resetConfirmKind === 'maptac') {
        const hoursLabel = (n) => (Number(n) === 1 ? '1 hour' : `${n} hours`)
        return [
          { label: 'Device Worn min', value: hoursLabel(MAPTAC_WARNING_THRESHOLDS.deviceWornHoursMin) },
          { label: 'Very Negative TAC min', value: hoursLabel(MAPTAC_WARNING_THRESHOLDS.veryNegativeTacHoursMin) },
          { label: 'Artifacts min', value: hoursLabel(MAPTAC_WARNING_THRESHOLDS.artifactHoursMin) }
        ]
      }
      return []
    },

    canExport() {
      return this.dayLevelData && this.dayLevelData.length > 0 && this.subjectInfo.subid
    },

    canExportDayLevelData() {
      return this.dayLevelData.length > 0
    },

    dayExportGroups() {
      return DAY_EXPORT_FEATURE_GROUPS
    },
    dayDescriptions() {
      return {
        ...dayFeatureDescriptions,
        ...buildReliabilityFlagDescriptions(this.maptacThresholds)
      }
    },
    dayLockedColumns() {
      const all = DAY_EXPORT_FEATURE_GROUPS.flatMap((g) =>
        g.sections ? g.sections.flatMap((s) => s.columns) : (g.columns || [])
      )
      return all.filter((c) => isLockedExportColumn(c, this.drinkingDayAlgorithmEnabled))
    },
    dayPresets() {
      const alg = this.drinkingDayAlgorithmEnabled
      return {
        essential: () => buildEssentialDayExportColumns(alg),
        defaults: () => buildDefaultDayExportColumns(alg),
        all: () => buildAllDayExportColumns()
      }
    },
    dayFilename() {
      const subid = this.subjectInfo.subid || 'Unknown'
      const datasetId = this.subjectInfo.dataset_identifier || '001'
      return `${subid}_${datasetId}_day_level_data.xlsx`
    },

    includedDays() {
      return this.dayLevelData.filter(day => !day.excluded)
    },

    averageDeviceTurnedOn() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, day) => sum + ((day.device_turned_on_percent_of_day || 0) * 100), 0)
      return (total / this.includedDays.length).toFixed(1)
    },

    averageDeviceWorn() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, day) => sum + ((day.device_worn_percent_of_day || 0) * 100), 0)
      return (total / this.includedDays.length).toFixed(1)
    },

    averageDeviceTurnedOnHours() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, day) => sum + (day.device_turned_on_duration || 0), 0)
      return (total / this.includedDays.length).toFixed(1)
    },

    averageDeviceWornHours() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, day) => sum + (day.device_worn_duration || 0), 0)
      return (total / this.includedDays.length).toFixed(1)
    },

    averageUsableDataHours() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, day) => {
        const dayHours = 24
        const lowQualityHours = day.low_quality_duration || 0
        return sum + (dayHours - lowQualityHours)
      }, 0)
      return (total / this.includedDays.length).toFixed(1)
    },

    lowQualityDataBreakdown() {
      if (!this.includedDays.length) return []
      
      const breakdown = {
        gaps: 0,
        nonWear: 0,
        jumps: 0,
        plummets: 0,
        extremeNegative: 0
      }
      
      this.includedDays.forEach(day => {
        breakdown.gaps += day.gap_duration || 0
        breakdown.nonWear += day.non_wear_duration || 0
        breakdown.jumps += day.jump_duration || 0
        breakdown.plummets += day.plummet_duration || 0
        breakdown.extremeNegative += day.extreme_negative_duration || 0
      })
      
      // Convert to average hours per day
      const days = this.includedDays.length
      return [
        { label: DATA_QUALITY_COLORS.gaps.label, value: (breakdown.gaps / days).toFixed(1), color: DATA_QUALITY_COLORS.gaps.backgroundColor },
        { label: DATA_QUALITY_COLORS.nonWear.label, value: (breakdown.nonWear / days).toFixed(1), color: DATA_QUALITY_COLORS.nonWear.backgroundColor },
        { label: DATA_QUALITY_COLORS.jumps.label, value: (breakdown.jumps / days).toFixed(1), color: DATA_QUALITY_COLORS.jumps.backgroundColor },
        { label: DATA_QUALITY_COLORS.plummets.label, value: (breakdown.plummets / days).toFixed(1), color: DATA_QUALITY_COLORS.plummets.backgroundColor },
        { label: DATA_QUALITY_COLORS.extremeNegative.label, value: (breakdown.extremeNegative / days).toFixed(1), color: DATA_QUALITY_COLORS.extremeNegative.backgroundColor }
      ].filter(item => parseFloat(item.value) > 0)
    },

    averageDataQuality() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, day) => {
        const dayHours = 24
        const lowQualityHours = (day.low_quality_duration || 0)
        const quality = Math.max(0, ((dayHours - lowQualityHours) / dayHours) * 100)
        return sum + quality
      }, 0)
      return (total / this.includedDays.length).toFixed(1)
    },

    // Summary tile metrics for anomalies (using pre-calculated backend percentages)
    averageJumpsHours() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, d) => sum + (Number(d.jump_duration) || 0), 0)
      return (total / this.includedDays.length).toFixed(1)
    },
    averagePlummetsHours() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, d) => sum + (Number(d.plummet_duration) || 0), 0)
      return (total / this.includedDays.length).toFixed(1)
    },
    averageExtremeNegativeHours() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, d) => sum + (Number(d.extreme_negative_duration) || 0), 0)
      return (total / this.includedDays.length).toFixed(1)
    },
    averageJumpsPct() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, day) => sum + ((day.jump_percent_of_day || 0) * 100), 0)
      return (total / this.includedDays.length).toFixed(1)
    },
    averagePlummetsPct() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, day) => sum + ((day.plummet_percent_of_day || 0) * 100), 0)
      return (total / this.includedDays.length).toFixed(1)
    },
    predictedDrinkingDaysCount() {
      return this.dayLevelData.filter(day => isPredictedDrinkingDay(day)).length
    },
    predictedDrinkingDaysPct() {
      const total = this.dayLevelData.length
      if (!total) return '0.0'
      return ((this.predictedDrinkingDaysCount / total) * 100).toFixed(1)
    },
    predictedNonDrinkingDaysCount() {
      return this.dayLevelData.filter(day => isPredictedNonDrinkingDay(day)).length
    },
    predictedNonDrinkingDaysPct() {
      const total = this.dayLevelData.length
      if (!total) return '0.0'
      return ((this.predictedNonDrinkingDaysCount / total) * 100).toFixed(1)
    },
    unreliablePredictionDaysCount() {
      return this.dayLevelData.filter(day => isUnreliablePredictionDay(day, this.maptacThresholds)).length
    },
    unreliablePredictionDaysPct() {
      const total = this.dayLevelData.length
      if (!total) return '0.0'
      return ((this.unreliablePredictionDaysCount / total) * 100).toFixed(1)
    },
    drinkingDays() {
      return this.dayLevelData.filter(day => isPredictedDrinkingDay(day))
    },
    nonDrinkingDays() {
      return this.dayLevelData.filter(day => isPredictedNonDrinkingDay(day))
    },
    drinkingHighConfidence() {
      return this.drinkingDays.filter(day => !isUnreliablePredictionDay(day, this.maptacThresholds))
    },
    drinkingLowConfidence() {
      return this.drinkingDays.filter(day => isUnreliablePredictionDay(day, this.maptacThresholds))
    },
    nonDrinkingHighConfidence() {
      return this.nonDrinkingDays.filter(day => !isUnreliablePredictionDay(day, this.maptacThresholds))
    },
    nonDrinkingLowConfidence() {
      return this.nonDrinkingDays.filter(day => isUnreliablePredictionDay(day, this.maptacThresholds))
    },
    nonWearWarningDaysCount() {
      return countWarningDays(this.dayLevelData, hasNonWearWarning).count
    },
    nonWearWarningDaysPct() {
      return countWarningDays(this.dayLevelData, hasNonWearWarning).pct
    },
    veryNegativeTacWarningDaysCount() {
      return countWarningDays(this.dayLevelData, hasVeryNegativeTacWarning).count
    },
    veryNegativeTacWarningDaysPct() {
      return countWarningDays(this.dayLevelData, hasVeryNegativeTacWarning).pct
    },
    artifactWarningDaysCount() {
      return countWarningDays(this.dayLevelData, hasArtifactWarning).count
    },
    artifactWarningDaysPct() {
      return countWarningDays(this.dayLevelData, hasArtifactWarning).pct
    },
    uncertainProbaWarningDaysCount() {
      return countWarningDays(this.dayLevelData, hasUncertainProbaWarning).count
    },
    uncertainProbaWarningDaysPct() {
      return countWarningDays(this.dayLevelData, hasUncertainProbaWarning).pct
    },
    averageArtifactsHours() {
      if (!this.includedDays.length) return '0.0'
      const total = this.includedDays.reduce((sum, day) => {
        return sum + (Number(day.jump_duration) || 0) + (Number(day.plummet_duration) || 0)
      }, 0)
      return (total / this.includedDays.length).toFixed(1)
    },
    averageArtifactsPct() {
      if (!this.includedDays.length) return '0.0'
      const total = this.includedDays.reduce((sum, day) => {
        return sum + ((Number(day.jump_percent_of_day) || 0) + (Number(day.plummet_percent_of_day) || 0)) * 100
      }, 0)
      return (total / this.includedDays.length).toFixed(1)
    },
    averageExtremeNegativePct() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, day) => sum + ((day.extreme_negative_percent_of_day || 0) * 100), 0)
      return (total / this.includedDays.length).toFixed(1)
    },
    averageNonWearHours() {
      if (!this.includedDays.length) return 0
      const total = this.includedDays.reduce((sum, d) => sum + (Number(d.non_wear_duration) || 0), 0)
      return (total / this.includedDays.length).toFixed(1)
    },
    averageNonWearPct() {
      if (!this.includedDays.length) return 0
      const hours = Number(this.averageNonWearHours)
      return ((hours / 24) * 100).toFixed(1)
    },

    averageTemperature() {
      if (!this.dayLevelData.length) return 0
      const total = this.dayLevelData.reduce((sum, day) => sum + (day.temp_mean || 0), 0)
      return total / this.dayLevelData.length
    },

    temperatureRange() {
      if (!this.dayLevelData.length) return 0
      const temps = this.dayLevelData.map(day => day.temp_max - day.temp_min).filter(val => !isNaN(val))
      if (!temps.length) return 0
      return Math.max(...temps)
    },

    averageTemperatureSD() {
      if (!this.dayLevelData.length) return 0
      const total = this.dayLevelData.reduce((sum, day) => sum + (day.temp_sd || 0), 0)
      return total / this.dayLevelData.length
    },

    averageMotion() {
      if (!this.dayLevelData.length) return 0
      const total = this.dayLevelData.reduce((sum, day) => sum + (day.motion_mean || 0), 0)
      return total / this.dayLevelData.length
    },

    motionRange() {
      if (!this.dayLevelData.length) return 0
      const motions = this.dayLevelData.map(day => day.motion_max - day.motion_min).filter(val => !isNaN(val))
      if (!motions.length) return 0
      return Math.max(...motions)
    },

    averageMotionSD() {
      if (!this.dayLevelData.length) return 0
      const total = this.dayLevelData.reduce((sum, day) => sum + (day.motion_sd || 0), 0)
      return total / this.dayLevelData.length
    },
    
    usedDaysCount() {
      return this.includedDays.length || 0
    },
    
    // Count days with issues
    adherentAndHealthyDaysCount() {
      return this.includedDays.filter(day => {
        const wornHours = Number(day.device_worn_duration || 0)
        const extremeNegHours = Number(day.extreme_negative_duration || 0)
        const isAdherent = wornHours >= this.thresholds.deviceWornHours
        const isHealthy = extremeNegHours <= this.thresholds.extremeNegativeMaxHours
        return isAdherent && isHealthy
      }).length
    },

    nonAdherentDaysCount() {
      return this.includedDays.filter(day => {
        const wornHours = Number(day.device_worn_duration || 0)
        return wornHours < this.thresholds.deviceWornHours
      }).length
    },
    
    lowHealthDaysCount() {
      return this.includedDays.filter(day => {
        const extremeNegHours = Number(day.extreme_negative_duration || 0)
        return extremeNegHours > this.thresholds.extremeNegativeMaxHours
      }).length
    },
    
    excludedDaysCount() {
      return this.dayLevelData.filter(day => !!day.excluded).length
    },
    
    // Percentage calculations
    adherentAndHealthyPercentage() {
      const total = this.dayLevelData.length || 0
      return total > 0 ? ((this.adherentAndHealthyDaysCount / total) * 100).toFixed(1) : '0.0'
    },

    nonAdherentPercentage() {
      const total = this.dayLevelData.length || 0
      return total > 0 ? ((this.nonAdherentDaysCount / total) * 100).toFixed(1) : '0.0'
    },
    
    lowHealthPercentage() {
      const total = this.dayLevelData.length || 0
      return total > 0 ? ((this.lowHealthDaysCount / total) * 100).toFixed(1) : '0.0'
    },
    
    excludedPercentage() {
      const total = this.dayLevelData.length || 0
      return total > 0 ? ((this.excludedDaysCount / total) * 100).toFixed(1) : '0.0'
    },
    // compute timeline bounds from expanded day data
    // Use the actual day boundaries (begin_day/end_day) if available, otherwise use data range
    computeTimelineStart() {
      return (data) => {
        // Try to get begin_day from the current expanded day
        if (this.expandedRowDate) {
          const day = this.dayLevelData.find(d => d.date === this.expandedRowDate)
          if (day && day.begin_day) {
            return day.begin_day
          }
        }
        // Fallback to first data point
        if (!data || !data.length) return null
        const first = data[0]
        return first?.datetime || null
      }
    },
    computeTimelineEnd() {
      return (data) => {
        // Try to get end_day from the current expanded day
        if (this.expandedRowDate) {
          const day = this.dayLevelData.find(d => d.date === this.expandedRowDate)
          if (day && day.end_day) {
            return day.end_day
          }
        }
        // Fallback to last data point
        if (!data || !data.length) return null
        const last = data[data.length - 1]
        return last?.datetime || null
      }
    }
  },

  methods: {
    getDrinkingDetectionForDay(day) {
      return resolveDrinkingDetectionForDay(day, { predictionThreshold: this.predictionThreshold })
    },

    getArtifactDurationHours(day) {
      return artifactDurationHours(day)
    },

    getArtifactPercentOfDay(day) {
      return (Number(day?.jump_percent_of_day) || 0) + (Number(day?.plummet_percent_of_day) || 0)
    },

    getArtifactsTextClass(day) {
      const hours = artifactDurationHours(day)
      const fail = hours >= Number(this.maptacThresholds.artifactHoursMin)
      return fail ? 'text-red-700' : 'text-gray-500'
    },

    downloadDayLevelData() {
      this.showDayColumnModal = true
    },

    async downloadReport() {
      try {
        // Validate data before export
        if (!validateAdherenceReportData({
          dayLevelData: this.dayLevelData,
          subjectInfo: this.subjectInfo
        })) {
          alert('Unable to export report: Missing required data')
          return
        }

        // Prepare summary statistics
        const summaryStats = {
          totalDays: this.dayLevelData.length,
          usedDays: this.usedDaysCount,
          averageDeviceTurnedOn: this.averageDeviceTurnedOn,
          averageDeviceTurnedOnHours: this.averageDeviceTurnedOnHours,
          averageDeviceWorn: this.averageDeviceWorn,
          averageDeviceWornHours: this.averageDeviceWornHours,
          averageJumpsPct: this.averageJumpsPct,
          averageJumpsHours: this.averageJumpsHours,
          averagePlummetsPct: this.averagePlummetsPct,
          averagePlummetsHours: this.averagePlummetsHours,
          averageExtremeNegativePct: this.averageExtremeNegativePct,
          averageExtremeNegativeHours: this.averageExtremeNegativeHours,
          nonAdherentDaysCount: this.nonAdherentDaysCount,
          nonAdherentPercentage: this.nonAdherentPercentage,
          lowHealthDaysCount: this.lowHealthDaysCount,
          lowHealthPercentage: this.lowHealthPercentage,
          adherentAndHealthyDaysCount: this.adherentAndHealthyDaysCount,
          adherentAndHealthyPercentage: this.adherentAndHealthyPercentage,
          excludedDaysCount: this.excludedDaysCount,
          excludedPercentage: this.excludedPercentage,
          averageTemperature: this.averageTemperature,
          temperatureRange: this.temperatureRange,
          averageTemperatureSD: this.averageTemperatureSD,
          averageMotion: this.averageMotion,
          motionRange: this.motionRange,
          averageMotionSD: this.averageMotionSD
        }

        // Call the export utility
        await downloadAdherenceReport({
          dayLevelData: this.dayLevelData,
          subjectInfo: this.subjectInfo,
          thresholds: this.thresholds,
          summaryStats: summaryStats
        })
      } catch (error) {
        console.error('Failed to download adherence report:', error)
        alert('Failed to download report. Please try again.')
      }
    },

    toggleTableExpansion() {
      this.isTableExpanded = !this.isTableExpanded
    },

    toggleExcludeDay(day) {
      const key = day?.date
      if (!key) return
      const newValue = !day.excluded
      day.excluded = newValue

      dataRevisionService.excludeDay(this.sessionId, this.s3Key, key, newValue)
        .then(() => {
          this.syncDayLevelDataToGlobalState()
        })
        .catch(err => {
          console.error('Failed to persist day exclusion — rolling back:', err)
          day.excluded = !newValue
        })
    },

    syncDayLevelDataToGlobalState() {
      this.syncProcessingResultsToGlobalState()
    },

    syncProcessingResultsToGlobalState(reviewBlock = null) {
      if (!this.globalState?.processingResults) return
      this.globalState.processingResults.day_level_data = this.dayLevelData
      const review = reviewBlock || buildDrinkingPredictionReview(
        this.predictionThreshold,
        this.maptacThresholds
      ).drinking_prediction_review
      this.globalState.processingResults.drinking_prediction_review = review
      this.updateGlobalState({ processingResults: this.globalState.processingResults })
    },

    syncAdherenceReviewToGlobalState(reviewBlock = null) {
      if (!this.globalState?.processingResults) return
      const review = reviewBlock || buildAdherenceReview(this.thresholds).adherence_review
      this.globalState.processingResults.adherence_review = review
      this.updateGlobalState({ processingResults: this.globalState.processingResults })
    },

    async persistDrinkingPredictionReview({ recomputePred = true } = {}) {
      const metadataPatch = buildDrinkingPredictionReview(
        this.predictionThreshold,
        this.maptacThresholds
      )
      const { revisions, updatesByDate } = buildDayRevisionPayloads(
        this.dayLevelData,
        this.predictionThreshold,
        this.maptacThresholds,
        { recomputePred }
      )

      await dataRevisionService.reviseDay(
        this.sessionId,
        this.s3Key,
        revisions,
        metadataPatch
      )

      if (updatesByDate.size) {
        this.dayLevelData = this.dayLevelData.map((day) => (
          updatesByDate.has(day.date) ? { ...updatesByDate.get(day.date) } : day
        ))
      }
      this.syncProcessingResultsToGlobalState(metadataPatch.drinking_prediction_review)
    },

    toggleDayChartType(day) {
      const key = day?.date
      if (!key) return
      // Toggle between 'signal' (default) and 'nonwear'
      const updated = { ...this.dayChartToggle }
      if (updated[key] === 'nonwear') {
        updated[key] = 'signal'
      } else {
        updated[key] = 'nonwear'
      }
      this.dayChartToggle = updated
    },

    getDayChartType(day) {
      const key = day?.date
      return this.dayChartToggle[key] || 'signal' // Default to signal processing chart
    },


    async applyThresholds() {
      this.adherenceApplying = true
      const prevThresholds = { ...this.thresholds }
      // Keep deviceOnHours synced to deviceWornHours for persisted metadata compatibility
      this.tempThresholds.deviceOnHours = Number(this.tempThresholds.deviceWornHours)
      this.thresholds = { ...this.tempThresholds }

      try {
        const metadataPatch = buildAdherenceReview(this.thresholds)
        await dataRevisionService.reviseMetadata(this.sessionId, this.s3Key, metadataPatch)
        this.syncAdherenceReviewToGlobalState(metadataPatch.adherence_review)
      } catch (err) {
        console.error('Failed to apply adherence thresholds — rolling back:', err)
        this.thresholds = prevThresholds
      } finally {
        this.adherenceApplying = false
      }
    },

    resetThresholds() {
      this.tempThresholds = { ...DEFAULT_ADHERENCE_THRESHOLDS }
      this.thresholds = { ...DEFAULT_ADHERENCE_THRESHOLDS }
    },

    async applyMaptacThresholds() {
      this.maptacApplying = true
      const prevThresholds = { ...this.maptacThresholds }
      this.maptacThresholds = {
        ...this.maptacThresholds,
        deviceWornHoursMin: this.tempMaptacThresholds.deviceWornHoursMin,
        veryNegativeTacHoursMin: this.tempMaptacThresholds.veryNegativeTacHoursMin,
        artifactHoursMin: this.tempMaptacThresholds.artifactHoursMin
      }

      try {
        await this.persistDrinkingPredictionReview({ recomputePred: false })
      } catch (err) {
        console.error('Failed to apply MAPTAC thresholds — rolling back:', err)
        this.maptacThresholds = prevThresholds
      } finally {
        this.maptacApplying = false
      }
    },

    resetMaptacThresholds() {
      this.tempMaptacThresholds = {
        ...this.tempMaptacThresholds,
        deviceWornHoursMin: MAPTAC_WARNING_THRESHOLDS.deviceWornHoursMin,
        veryNegativeTacHoursMin: MAPTAC_WARNING_THRESHOLDS.veryNegativeTacHoursMin,
        artifactHoursMin: MAPTAC_WARNING_THRESHOLDS.artifactHoursMin
      }
      this.maptacThresholds = {
        ...this.maptacThresholds,
        deviceWornHoursMin: MAPTAC_WARNING_THRESHOLDS.deviceWornHoursMin,
        veryNegativeTacHoursMin: MAPTAC_WARNING_THRESHOLDS.veryNegativeTacHoursMin,
        artifactHoursMin: MAPTAC_WARNING_THRESHOLDS.artifactHoursMin
      }
    },

    async applyPredictionThreshold() {
      this.predictionThresholdApplying = true
      const prevThreshold = this.predictionThreshold
      const prevMaptac = { ...this.maptacThresholds }
      this.predictionThreshold = this.tempPredictionThreshold
      this.maptacThresholds = {
        ...this.maptacThresholds,
        probaUnreliableLow: this.tempMaptacThresholds.probaUnreliableLow,
        probaUnreliableHigh: this.tempMaptacThresholds.probaUnreliableHigh
      }

      try {
        await this.persistDrinkingPredictionReview({ recomputePred: true })
      } catch (err) {
        console.error('Failed to apply prediction threshold — rolling back:', err)
        this.predictionThreshold = prevThreshold
        this.maptacThresholds = prevMaptac
      } finally {
        this.predictionThresholdApplying = false
      }
    },

    resetPredictionThreshold() {
      this.tempPredictionThreshold = DEFAULT_PREDICTION_THRESHOLD
      this.predictionThreshold = DEFAULT_PREDICTION_THRESHOLD
      this.tempMaptacThresholds = {
        ...this.tempMaptacThresholds,
        probaUnreliableLow: MAPTAC_WARNING_THRESHOLDS.probaUnreliableLow,
        probaUnreliableHigh: MAPTAC_WARNING_THRESHOLDS.probaUnreliableHigh
      }
      this.maptacThresholds = {
        ...this.maptacThresholds,
        probaUnreliableLow: MAPTAC_WARNING_THRESHOLDS.probaUnreliableLow,
        probaUnreliableHigh: MAPTAC_WARNING_THRESHOLDS.probaUnreliableHigh
      }
    },

    openResetConfirm(kind) {
      this.resetConfirmKind = kind
      this.showResetConfirm = true
    },

    closeResetConfirm() {
      if (this.resetApplying) return
      this.showResetConfirm = false
      this.resetConfirmKind = null
    },

    async confirmApplyDefaults() {
      if (this.resetApplying || !this.resetConfirmKind) return
      this.resetApplying = true
      const kind = this.resetConfirmKind

      try {
        if (kind === 'adherence') {
          const prevThresholds = { ...this.thresholds }
          this.resetThresholds()
          try {
            const metadataPatch = buildAdherenceReview(this.thresholds)
            await dataRevisionService.reviseMetadata(this.sessionId, this.s3Key, metadataPatch)
            this.syncAdherenceReviewToGlobalState(metadataPatch.adherence_review)
          } catch (err) {
            console.error('Failed to apply adherence defaults — rolling back:', err)
            this.thresholds = prevThresholds
            throw err
          }
        } else if (kind === 'prediction') {
          const prevThreshold = this.predictionThreshold
          const prevMaptac = { ...this.maptacThresholds }
          this.resetPredictionThreshold()
          try {
            await this.persistDrinkingPredictionReview({ recomputePred: true })
          } catch (err) {
            console.error('Failed to apply prediction defaults — rolling back:', err)
            this.predictionThreshold = prevThreshold
            this.maptacThresholds = prevMaptac
            throw err
          }
        } else if (kind === 'maptac') {
          const prevThresholds = { ...this.maptacThresholds }
          this.resetMaptacThresholds()
          try {
            await this.persistDrinkingPredictionReview({ recomputePred: false })
          } catch (err) {
            console.error('Failed to apply MAPTAC defaults — rolling back:', err)
            this.maptacThresholds = prevThresholds
            throw err
          }
        }

        this.showResetConfirm = false
        this.resetConfirmKind = null
      } catch (_) {
        // Keep modal open; applied values already rolled back
      } finally {
        this.resetApplying = false
      }
    },

    predictionValueToPct(value) {
      return Number(value) * 100
    },

    roundProba(value) {
      return Math.round(Number(value) * 100) / 100
    },

    snapToOptions(value, options) {
      if (!options.length) return this.roundProba(value)
      let best = options[0]
      let bestDist = Infinity
      for (const opt of options) {
        const dist = Math.abs(opt - value)
        if (dist < bestDist) {
          bestDist = dist
          best = opt
        }
      }
      return best
    },

    thresholdClampRange() {
      const low = Number(this.tempMaptacThresholds.probaUnreliableLow)
      const high = Number(this.tempMaptacThresholds.probaUnreliableHigh)
      // Keep threshold ≥0.05 from either bound, and within global 0.05–0.95
      const minT = Math.max(0.05, this.roundProba(low + MIN_THRESHOLD_BOUND_GAP))
      const maxT = Math.min(0.95, this.roundProba(high - MIN_THRESHOLD_BOUND_GAP))
      if (minT >= maxT) {
        const mid = this.roundProba((low + high) / 2)
        const fallback = Math.min(0.95, Math.max(0.05, mid))
        return { minT: fallback, maxT: fallback, low, high }
      }
      return { minT, maxT, low, high }
    },

    clampThresholdValue(value) {
      const { minT, maxT, low, high } = this.thresholdClampRange()
      let v = this.roundProba(value)
      if (v < minT) v = minT
      if (v > maxT) v = maxT
      // Final invariant: at least MIN_THRESHOLD_BOUND_GAP from each bound
      if (this.roundProba(v - low) < MIN_THRESHOLD_BOUND_GAP || this.roundProba(high - v) < MIN_THRESHOLD_BOUND_GAP) {
        v = minT === maxT ? minT : this.roundProba((minT + maxT) / 2)
      }
      return v
    },

    syncThresholdInputElement(el, clamped) {
      if (!el) return
      // Native range can move the thumb before Vue re-renders; when the clamped
      // value equals the prior Vue state, :value won't update the DOM.
      el.value = String(clamped)
    },

    onThresholdInput(event) {
      const clamped = this.clampThresholdValue(event.target.value)
      this.tempPredictionThreshold = clamped
      this.syncThresholdInputElement(event.target, clamped)
      this.$nextTick(() => {
        const el = this.$refs.predictionThresholdInput || event.target
        this.syncThresholdInputElement(el, this.clampThresholdValue(this.tempPredictionThreshold))
      })
    },

    startBoundDrag(which, event) {
      event.preventDefault()
      event.stopPropagation()
      this.boundDrag = { which }
      window.addEventListener('pointermove', this.onBoundDragMove)
      window.addEventListener('pointerup', this.onBoundDragEnd)
      window.addEventListener('pointercancel', this.onBoundDragEnd)
      this.onBoundDragMove(event)
    },

    allowedLowBoundOptions(threshold = this.tempPredictionThreshold) {
      const maxLow = this.roundProba(Number(threshold) - MIN_THRESHOLD_BOUND_GAP)
      return this.probaLowSnapOptions.filter(v => v <= maxLow)
    },

    allowedHighBoundOptions(threshold = this.tempPredictionThreshold) {
      const minHigh = this.roundProba(Number(threshold) + MIN_THRESHOLD_BOUND_GAP)
      return this.probaHighSnapOptions.filter(v => v >= minHigh)
    },

    onBoundDragMove(event) {
      if (!this.boundDrag) return
      const track = this.$refs.predictionSliderTrack
      if (!track) return
      const rect = track.getBoundingClientRect()
      if (rect.width <= 0) return
      const raw = this.roundProba(Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)))
      const threshold = Number(this.tempPredictionThreshold)

      if (this.boundDrag.which === 'low') {
        const allowed = this.allowedLowBoundOptions(threshold)
        if (!allowed.length) return
        const next = this.snapToOptions(raw, allowed)
        if (this.roundProba(threshold - next) < MIN_THRESHOLD_BOUND_GAP) return
        this.tempMaptacThresholds.probaUnreliableLow = next
      } else {
        const allowed = this.allowedHighBoundOptions(threshold)
        if (!allowed.length) return
        const next = this.snapToOptions(raw, allowed)
        if (this.roundProba(next - threshold) < MIN_THRESHOLD_BOUND_GAP) return
        this.tempMaptacThresholds.probaUnreliableHigh = next
      }
      this.tempPredictionThreshold = this.clampThresholdValue(this.tempPredictionThreshold)
    },

    onBoundDragEnd() {
      this.boundDrag = null
      window.removeEventListener('pointermove', this.onBoundDragMove)
      window.removeEventListener('pointerup', this.onBoundDragEnd)
      window.removeEventListener('pointercancel', this.onBoundDragEnd)
    },

    clampPredictionControls() {
      let threshold = Math.min(0.95, Math.max(0.05, this.roundProba(this.tempPredictionThreshold)))
      let low = Number(this.tempMaptacThresholds.probaUnreliableLow)
      let high = Number(this.tempMaptacThresholds.probaUnreliableHigh)

      const lowAllowed = this.allowedLowBoundOptions(threshold)
      const highAllowed = this.allowedHighBoundOptions(threshold)

      if (lowAllowed.length) {
        low = lowAllowed.includes(low) ? low : this.snapToOptions(low, lowAllowed)
        if (this.roundProba(threshold - low) < MIN_THRESHOLD_BOUND_GAP) {
          low = lowAllowed[lowAllowed.length - 1]
        }
      }
      if (highAllowed.length) {
        high = highAllowed.includes(high) ? high : this.snapToOptions(high, highAllowed)
        if (this.roundProba(high - threshold) < MIN_THRESHOLD_BOUND_GAP) {
          high = highAllowed[0]
        }
      }

      this.tempMaptacThresholds = {
        ...this.tempMaptacThresholds,
        probaUnreliableLow: low,
        probaUnreliableHigh: high
      }
      threshold = this.clampThresholdValue(threshold)
      this.tempPredictionThreshold = threshold

      this.maptacThresholds = {
        ...this.maptacThresholds,
        probaUnreliableLow: low,
        probaUnreliableHigh: high
      }
      const applied = this.clampThresholdValue(this.predictionThreshold)
      const gapOk = (
        this.roundProba(applied - low) >= MIN_THRESHOLD_BOUND_GAP
        && this.roundProba(high - applied) >= MIN_THRESHOLD_BOUND_GAP
      )
      this.predictionThreshold = gapOk ? applied : threshold
    },
    async toggleRow(day) {
      const newExpanded = day && day.date ? day.date : null
      // Collapse if clicking the same row
      if (this.expandedRowDate === newExpanded) {
        this.cleanupExpandedState()
        return
      }

      // Switching rows: cleanup previous, then fetch new
      this.cleanupExpandedState()

      this.expandedRowDate = newExpanded
      if (!newExpanded) return

      try {
        this.expandedDayLoading = true
        this.expandedDayError = null

        // Check cache first (Phase 1 optimization)
        const cacheKey = day.date
        const cachedData = this.dayDataCache.get(cacheKey)
        if (cachedData) {
          this.expandedDayData = cachedData
          this.expandedComponent = 'SignalProcessingChart'
          this.expandedDayLoading = false
          return
        }

        // Load processing results to get session_id or s3_key
        let sessionId = this.sessionId
        let s3Key = null
        
        if (!sessionId) {
          const processingResults = sessionStorage.getItem('processingResults')
          if (processingResults) {
            const results = JSON.parse(processingResults)
            sessionId = results.session_id || null
            s3Key = results.s3_key || null
          }
        } else {
          // If sessionId exists, also check for s3_key in case it's a prior job
          const processingResults = sessionStorage.getItem('processingResults')
          if (processingResults) {
            const results = JSON.parse(processingResults)
            s3Key = results.s3_key || null
          }
        }

        // Check if we have either session_id OR s3_key (for prior jobs)
        if (!sessionId && !s3Key) {
          this.expandedDayError = 'No active session or prior job data. Please process data first.'
          this.expandedDayLoading = false
          return
        }

        // Determine day start/end range for the clicked day
        // Use begin_day and end_day from day-level data if available (respects day_start_hour)
        // Otherwise fall back to midnight boundaries
        let start, end
        if (day.begin_day && day.end_day) {
          start = day.begin_day
          end = day.end_day
        } else {
          // Fallback to midnight boundaries if begin_day/end_day not available
          start = this.normalizeDayStart(day.date)
          end = this.normalizeDayEnd(day.date)
        }

        // Build request payload - use s3_key if available (for prior results), otherwise use session_id
        // Phase 1: Include columns parameter for optimization
        const requestPayload = {
          start_time: start,
          end_time: end,
          columns: ALL_CHART_COLUMNS  // Request only columns needed for charts (~15 cols vs ~50 cols)
        }
        
        if (s3Key) {
          // Prior results: use s3_key for direct S3 access
          requestPayload.s3_key = s3Key
          console.log('📦 Using s3_key for day data fetch (prior result):', s3Key)
        } else if (sessionId) {
          // Active session: use session_id
          requestPayload.session_id = sessionId
          console.log('📦 Using session_id for day data fetch (active session):', sessionId)
        }

        const response = await fetch(buildApiUrl(API_ENDPOINTS.GET_DATA_FOR_TIME_RANGE), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestPayload)
        })

        const json = await response.json()
        if (!response.ok || !json.success) {
          throw new Error(json.message || 'Failed to fetch day data')
        }

        const payload = json.data || {}
        const fetchedData = payload.filtered_data || []
        
        // Store in cache (Phase 1)
        this.dayDataCache.set(cacheKey, fetchedData)
        
        this.expandedDayData = fetchedData

        // Prepare chart props
        this.expandedComponent = 'SignalProcessingChart'
      } catch (e) {
        this.expandedDayError = e?.message || 'Error loading day data'
        this.expandedRowDate = null
      } finally {
        this.expandedDayLoading = false
      }
    },

    cleanupExpandedState() {
      this.expandedRowDate = null
      this.expandedComponent = null
      this.expandedDayData = null
      this.expandedDayError = null
      this.expandedDayLoading = false
    },

    normalizeDayStart(dateString) {
      const datePart = this.extractDatePart(dateString)
      return datePart ? `${datePart}T00:00:00` : dateString
    },

    normalizeDayEnd(dateString) {
      const datePart = this.extractDatePart(dateString)
      return datePart ? `${datePart}T23:59:59` : dateString
    },

    extractDatePart(input) {
      if (!input) return null
      const str = String(input)
      const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (m) {
        // Already in YYYY-MM-DD format
        return `${m[1]}-${m[2]}-${m[3]}`
      }
      // Fallback: try to parse without converting to ISO (build from parts)
      try {
        const d = new Date(str)
        if (isNaN(d.getTime())) return null
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd}`
      } catch (_) {
        return null
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        // Handle date-only strings (YYYY-MM-DD) by appending time component
        // JavaScript's Date() interprets date-only strings as UTC, causing timezone shifts
        // By adding 'T00:00:00', we ensure it's parsed as local time (matching begin_day)
        let dateInput = dateString
        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          // Date-only string (YYYY-MM-DD) - append time to parse as local time
          dateInput = dateString + 'T00:00:00'
        }
        const date = new Date(dateInput)
        if (isNaN(date.getTime())) return dateString
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        })
      } catch (error) {
        return dateString
      }
    },
    parseDayTime(dateTimeString) {
      if (!dateTimeString) return null
      try {
        const date = new Date(dateTimeString)
        if (isNaN(date.getTime())) return null

        const dow = date.toLocaleDateString('en-US', { weekday: 'short' })
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const year = String(date.getFullYear()).slice(-2)

        let hour = date.getHours()
        const period = hour >= 12 ? 'PM' : 'AM'
        hour = hour % 12
        hour = hour === 0 ? 12 : hour

        return { dow, date: `${month}/${day}/${year}`, time: `${hour}${period}` }
      } catch {
        return null
      }
    },

    // Helper to safely convert value to number and format with decimal places
    safeToFixed(value, decimals = 1) {
      const num = Number(value || 0)
      if (isNaN(num)) return '0.0'
      return num.toFixed(decimals)
    },

    // Adherence: device worn >= threshold (binding constraint; also used for Powered On column highlighting)
    getAdherenceLabel(day) {
      if (day?.excluded) return 'Excluded'
      const wornHours = Number(day.device_worn_duration || 0)
      const pass = wornHours >= this.thresholds.deviceWornHours
      return pass ? 'Adherent' : 'Not Adherent'
    },
    getAdherenceBadgeClass(day) {
      if (day?.excluded) return 'bg-gray-200 text-gray-600'
      const wornHours = Number(day.device_worn_duration || 0)
      const pass = wornHours >= this.thresholds.deviceWornHours
      return pass ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    },

    // Biosensor Health: extreme negative duration <= threshold
    getBiosensorHealthLabel(day) {
      if (day?.excluded) return 'Excluded'
      const extremeNegHours = Number(day.extreme_negative_duration || 0)
      const pass = extremeNegHours <= this.thresholds.extremeNegativeMaxHours
      return pass ? 'Healthy' : 'Unhealthy'
    },
    getBiosensorHealthBadgeClass(day) {
      if (day?.excluded) return 'bg-gray-200 text-gray-600'
      const extremeNegHours = Number(day.extreme_negative_duration || 0)
      const pass = extremeNegHours <= this.thresholds.extremeNegativeMaxHours
      return pass ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    },

    getQualityScore(day) {
      const dayHours = 24
      const lowQualityHours = day.low_quality_duration || 0
      const quality = Math.max(0, ((dayHours - lowQualityHours) / dayHours) * 100)
      const score = Math.round(quality)
      
      if (score >= 90) return 'Excellent'
      if (score >= 80) return 'Good'
      if (score >= 70) return 'Fair'
      if (score >= 60) return 'Poor'
      return 'Very Poor'
    },

    getQualityScoreClass(day) {
      const dayHours = 24
      const lowQualityHours = day.low_quality_duration || 0
      const quality = Math.max(0, ((dayHours - lowQualityHours) / dayHours) * 100)
      const score = Math.round(quality)
      
      if (score >= 90) return 'bg-green-100 text-green-800'
      if (score >= 80) return 'bg-blue-100 text-blue-800'
      if (score >= 70) return 'bg-yellow-100 text-yellow-800'
      if (score >= 60) return 'bg-orange-100 text-orange-800'
      return 'bg-red-100 text-red-800'
    },

    // Table text styling helpers based on applied thresholds for the active view
    appliedDeviceWornHoursMin() {
      return this.trackingView === 'drinking'
        ? Number(this.maptacThresholds.deviceWornHoursMin)
        : Number(this.thresholds.deviceWornHours)
    },

    getOnTextClass(day) {
      const onHours = Number(day?.device_turned_on_duration || 0)
      const fail = onHours < this.appliedDeviceWornHoursMin()
      return fail ? 'text-red-700' : 'text-gray-500'
    },
    getOnSubTextClass(day) {
      const onHours = Number(day?.device_turned_on_duration || 0)
      const fail = onHours < this.appliedDeviceWornHoursMin()
      return fail ? 'text-red-700' : 'text-gray-400'
    },
    getWornTextClass(day) {
      const wornHours = Number(day?.device_worn_duration || 0)
      const fail = wornHours < this.appliedDeviceWornHoursMin()
      return fail ? 'text-red-700' : 'text-gray-500'
    },
    getWornSubTextClass(day) {
      const wornHours = Number(day?.device_worn_duration || 0)
      const fail = wornHours < this.appliedDeviceWornHoursMin()
      return fail ? 'text-red-700' : 'text-gray-400'
    },
    getExtremeNegTextClass(day) {
      const extremeNegHours = Number(day?.extreme_negative_duration || 0)
      const fail = this.trackingView === 'drinking'
        ? extremeNegHours >= Number(this.maptacThresholds.veryNegativeTacHoursMin)
        : extremeNegHours > Number(this.thresholds.extremeNegativeMaxHours)
      return fail ? 'text-red-700' : 'text-gray-500'
    },

    getPredictionTextClass(day) {
      return this.getPredictionWarningReasons(day).length
        ? 'text-[#800020]'
        : 'text-gray-700'
    },

    getPredictionWarningReasons(day) {
      return getDrinkingPredictionWarningReasons(day, this.maptacThresholds)
    },

  },

  mounted() {
    // Set up back button to return to dashboard
    this.setBackButton({
      text: 'Return to Dashboard',
      route: '/subject-dashboard'
    })
    
    // Load data - check globalState first (most up-to-date), then fall back to sessionStorage
    try {
      let processingResults = null
      
      // Try globalState first (most recent data)
      if (this.globalState?.processingResults) {
        processingResults = this.globalState.processingResults
        console.log('✅ Loaded processing results from globalState')
      } else {
        // Fallback to sessionStorage
        const storedResults = sessionStorage.getItem('processingResults')
        if (storedResults) {
          processingResults = JSON.parse(storedResults)
          console.log('✅ Loaded processing results from sessionStorage')
        }
      }
      
      if (processingResults) {
        this.dayLevelData = processingResults.day_level_data || []
        this.drinkingDayAlgorithmEnabled = Boolean(
          processingResults.column_metadata?.drinking_day_algorithm_enabled
        )
        const hydrated = hydrateDrinkingPredictionReview(processingResults)
        this.predictionThreshold = hydrated.predictionThreshold
        this.tempPredictionThreshold = hydrated.predictionThreshold
        this.maptacThresholds = { ...hydrated.maptacThresholds }
        this.tempMaptacThresholds = { ...hydrated.maptacThresholds }
        this.clampPredictionControls()
        const adherenceHydrated = hydrateAdherenceReview(processingResults)
        this.thresholds = { ...adherenceHydrated }
        this.tempThresholds = { ...adherenceHydrated }
        this.trackingView = this.hasDrinkingPredictions ? 'drinking' : 'adherence'
        this.subjectInfo = {
          subid: processingResults.subid,
          dataset_identifier: processingResults.dataset_identifier
        }
        this.sessionId = processingResults.session_id || null
        this.s3Key = processingResults.s3_key || null
        
        console.log('Loaded adherence report data:', {
          dayLevelDataLength: this.dayLevelData.length,
          subjectInfo: this.subjectInfo
        })
        
        // Debug: Log the first day's data to see what columns we actually have
        if (this.dayLevelData.length > 0) {
          const firstDay = this.dayLevelData[0]
          console.log('First day data sample:', firstDay)
          console.log('Available columns:', Object.keys(firstDay))
          
          // Debug: Check percentage values specifically
          console.log('🔍 Percentage values (first day):', {
            device_turned_on_duration: firstDay.device_turned_on_duration,
            device_turned_on_percent_of_day: firstDay.device_turned_on_percent_of_day,
            device_turned_on_percent_of_day_type: typeof firstDay.device_turned_on_percent_of_day,
            device_turned_on_percent_of_day_calc: (firstDay.device_turned_on_duration / 24),
            device_worn_duration: firstDay.device_worn_duration,
            device_worn_percent_of_day: firstDay.device_worn_percent_of_day,
            device_worn_percent_of_day_type: typeof firstDay.device_worn_percent_of_day,
            extreme_negative_duration: firstDay.extreme_negative_duration,
            extreme_negative_percent_of_day: firstDay.extreme_negative_percent_of_day,
            extreme_negative_percent_of_day_type: typeof firstDay.extreme_negative_percent_of_day,
            extreme_negative_percent_of_day_calc: (firstDay.extreme_negative_duration / 24)
          })
          
          // Debug: Check begin_day and end_day values
          if (firstDay.begin_day || firstDay.end_day) {
            console.log('🔍 Day boundaries (first day):', {
              begin_day: firstDay.begin_day,
              end_day: firstDay.end_day,
              date: firstDay.date
            })
          } else {
            console.warn('⚠️ begin_day/end_day not found in day-level data!')
          }
        }
      } else {
        console.warn('No processing results found in sessionStorage')
      }
    } catch (error) {
      console.error('Error loading adherence report data:', error)
    }
  },

  beforeUnmount() {
    this.onBoundDragEnd()
    // Clear day data cache when leaving the page (Phase 1)
    this.dayDataCache.clear()
  }
}
</script>

<style scoped>
.daily-breakdown-table :is(th, td).db-col-day {
  width: 3rem;
  padding-left: 0.4rem;
  padding-right: 0.4rem;
}

.daily-breakdown-table :is(th, td).db-col-time {
  width: 1%;
  min-width: 0;
  padding-left: 0.35rem;
  padding-right: 0.35rem;
  white-space: nowrap;
}

.daily-breakdown-table :is(th, td).db-col-prediction,
.daily-breakdown-table :is(th, td).db-col-metric {
  width: 11rem;
  min-width: 11rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.daily-breakdown-table :is(th, td).db-col-badge {
  width: 7rem;
  min-width: 7rem;
  max-width: 7.5rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.daily-breakdown-table :is(th, td).db-col-exclude {
  width: 5rem;
  padding-left: 1rem;
  padding-right: 1rem;
}

/* Column group outlines (adherence view): Adherence block + Health block
   Use a single border per vertical divider (no adjacent left+right = double line). */
.daily-breakdown-table {
  border-collapse: separate;
  border-spacing: 0;
}

.daily-breakdown-table :is(th, td).db-group-adherence-start,
.daily-breakdown-table :is(th, td).db-group-health-start {
  border-left: 1.5px solid #d1d5db;
}

.daily-breakdown-table :is(th, td).db-group-health-end {
  border-right: 1.5px solid #d1d5db;
}

.daily-breakdown-table :is(th, td).db-group-adherence-start,
.daily-breakdown-table :is(th, td).db-group-adherence-mid,
.daily-breakdown-table :is(th, td).db-group-adherence-end,
.daily-breakdown-table :is(th, td).db-group-health-start,
.daily-breakdown-table :is(th, td).db-group-health-end {
  text-align: center;
  vertical-align: middle;
}

/* Equal gutter on both sides of each group divider */
.daily-breakdown-table :is(th, td).db-before-group {
  padding-right: 1.15rem;
}

.daily-breakdown-table :is(th, td).db-group-adherence-start,
.daily-breakdown-table :is(th, td).db-group-health-start {
  padding-left: 1.15rem;
}

.daily-breakdown-table :is(th, td).db-group-adherence-end,
.daily-breakdown-table :is(th, td).db-group-health-end {
  padding-right: 1.15rem;
}

.daily-breakdown-table :is(th, td).db-col-exclude {
  padding-left: 1.15rem;
}

.daily-breakdown-table tr.db-row-excluded > td {
  background-color: #f3f4f6;
  opacity: 0.65;
  filter: grayscale(1);
  color: #6b7280;
}

.daily-breakdown-table tr.db-row-excluded > td.db-col-exclude {
  filter: none;
  opacity: 1;
}

.prediction-threshold-slider {
  padding-top: 1.25rem;
}

.prediction-slider-track {
  height: 1.5rem;
  display: flex;
  align-items: center;
}

.prediction-uncertain-band {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 14px;
  border-radius: 4px;
  background: rgba(128, 0, 32, 0.28);
  pointer-events: none;
  z-index: 1;
}

.prediction-bound-marker {
  position: absolute;
  /* Anchor near track center but extend mostly upward for easier grab */
  top: 50%;
  transform: translate(-50%, calc(-50% - 12px));
  width: 28px;
  height: 48px;
  z-index: 25;
  cursor: ew-resize;
  touch-action: none;
}

.prediction-bound-marker :deep(.teleport-tooltip),
.prediction-bound-marker :deep(.teleport-tooltip-trigger) {
  display: block;
  width: 100%;
  height: 100%;
}

.prediction-bound-hit {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
  cursor: ew-resize;
}

.prediction-bound-line {
  position: absolute;
  left: 50%;
  /* Keep the visible tick crossing the bar (lower portion of tall hit target) */
  bottom: 4px;
  width: 2px;
  height: 28px;
  background: #6d28d9;
  transform: translateX(-50%);
  pointer-events: none;
  border-radius: 1px;
}

.prediction-bound-label {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.65rem;
  font-weight: 500;
  color: #6d28d9;
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
  font-variant-numeric: tabular-nums;
}

.prediction-threshold-value {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 0.75rem;
  font-weight: 600;
  color: #6d28d9;
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
  z-index: 25;
  font-variant-numeric: tabular-nums;
}

.prediction-threshold-slider input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  border-radius: 3px;
  outline: none;
  background: transparent;
}
.prediction-threshold-slider input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #7c3aed;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.25);
  cursor: pointer;
  transition: transform 0.15s ease;
  position: relative;
  z-index: 20;
}
.prediction-threshold-slider input[type="range"]::-webkit-slider-thumb:hover {
  transform: scale(1.15);
}
.prediction-threshold-slider input[type="range"]::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #7c3aed;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.25);
  cursor: pointer;
}
.prediction-threshold-slider input[type="range"]::-moz-range-track {
  height: 6px;
  border-radius: 3px;
}
</style>
